import type {
  IWorkJourneyMatrixCell,
  IWorkJourneyMatrixUser,
  TWorkJourneyMatrixCellKind,
} from '@src/models/interfaces/workJourneyManagement.interface';
import type { WorkJourneyWorkShift } from '@src/packages/workJourney/types';

type ShiftHoursSource = Pick<WorkJourneyWorkShift, 'daysOfWeek' | 'recurrence'> | null | undefined;

const parseHmToMinutes = (value?: string): number | null => {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) {
    return null;
  }
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
};

const firstActiveRecurrenceDay = (workShift: ShiftHoursSource) => {
  const firstWeek = workShift?.recurrence?.weeks?.[0];
  if (!firstWeek) return undefined;
  return Object.values(firstWeek.days ?? {}).find(
    day => day?.isActive === true && day.startTime && day.endTime,
  );
};

/**
 * Estimated hours for one scheduled workday when no attendance record exists.
 * Mirrors Services `estimateDailyShiftHours`.
 */
export const estimateDailyShiftHours = (workShift?: ShiftHoursSource): number => {
  if (!workShift) {
    return 0;
  }

  const activeDay = firstActiveRecurrenceDay(workShift);
  if (activeDay?.startTime && activeDay?.endTime) {
    const start = parseHmToMinutes(activeDay.startTime);
    const end = parseHmToMinutes(activeDay.endTime);
    if (start != null && end != null && end > start) {
      const breakMinutes = typeof activeDay.breakTime === 'number' ? activeDay.breakTime : 0;
      return Math.max(0, (end - start - breakMinutes) / 60);
    }
  }

  const daysOfWeek = workShift.daysOfWeek ?? [];
  const weekly = Number(workShift.recurrence?.totalShiftHours) || 0;
  if (weekly > 0 && daysOfWeek.length > 0) {
    return weekly / daysOfWeek.length;
  }

  return 0;
};

/** Break minutes from the first active recurrence day. Mirrors Services. */
export const resolveShiftBreakMinutes = (workShift?: ShiftHoursSource): number => {
  const activeDay = firstActiveRecurrenceDay(workShift);
  return typeof activeDay?.breakTime === 'number' ? activeDay.breakTime : 0;
};

/**
 * Hours credited by an exception-only cell (no AR scheduled times).
 * Only inbound REM adds the daily estimate. Mirrors Services `exceptionCellHours`.
 */
export const exceptionCellHours = (
  kind: TWorkJourneyMatrixCellKind,
  dailyEstimate: number,
): number => (kind === 'replacement' ? dailyEstimate : 0);

/**
 * Hours one matrix cell contributes to `programmedHours`.
 * Prefers AR scheduled window when present; else planned/work/REM estimate; else 0.
 * Stub `attendance` objects from optimistic patches (no scheduled times) fall through
 * to kind-based rules so absences drop hours and planned days keep the estimate.
 */
export const hoursFromMatrixCell = (
  cell: Pick<IWorkJourneyMatrixCell, 'kind' | 'attendance'>,
  dailyEstimate: number,
  breakMinutes = 0,
): number => {
  const start = cell.attendance?.scheduledStartAtSeconds;
  const end = cell.attendance?.scheduledEndAtSeconds;
  if (start != null && end != null && end > start) {
    return Math.max(0, (end - start) / 3600 - breakMinutes / 60);
  }
  if (cell.kind === 'planned' || cell.kind === 'work') {
    return dailyEstimate;
  }
  return exceptionCellHours(cell.kind, dailyEstimate);
};

export type RecomputeMatrixProgrammedHoursParams = {
  users: readonly IWorkJourneyMatrixUser[];
  cells: readonly IWorkJourneyMatrixCell[];
  dailyEstimate: number;
  breakMinutes?: number;
};

/**
 * Rebuilds each row's `programmedHours` from current cells so optimistic
 * exception/attendance patches stay aligned with Services without a reload.
 */
export const recomputeMatrixProgrammedHours = ({
  users,
  cells,
  dailyEstimate,
  breakMinutes = 0,
}: RecomputeMatrixProgrammedHoursParams): IWorkJourneyMatrixUser[] => {
  const hoursByUser = new Map<string, number>();

  cells.forEach(cell => {
    if (!cell.userId) return;
    const next =
      (hoursByUser.get(cell.userId) ?? 0) +
      hoursFromMatrixCell(cell, dailyEstimate, breakMinutes);
    hoursByUser.set(cell.userId, next);
  });

  return users.map(user => ({
    ...user,
    programmedHours: Math.round((hoursByUser.get(user.id) ?? 0) * 10) / 10,
  }));
};
